import React, { useState, useEffect } from 'react';
import api from '../api/client';
import SecondaryTable from './SecondaryTable';
import Pagination from './TablePagination';

const SORT_ORDER = {
  NONE: 'NONE',
  ASC: 'ASC',
  DESC: 'DESC',
};

const DropdownCell = ({ value, endpoint, row, paramKey = 'seedsID' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [displayValue, setDisplayValue] = useState('Loading...');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get(endpoint, {
          params: { [paramKey]: row[paramKey] },
        });

        if (response.data.length > 0) {
          const firstOption = response.data[0];
          setDisplayValue(`${firstOption.packets} (${firstOption.displayWeight}${firstOption.unit})`);
        } else {
          setDisplayValue('No options');
        }
        setOptions(response.data);
      } catch (error) {
        console.error('Fetch error:', error);
        setDisplayValue('Error');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [row[paramKey], endpoint]);

  return (
    <div className="dropdown-cell">
      <div className="dropdown-header" onClick={() => setIsOpen(!isOpen)}>
        {loading ? 'Calculating...' : displayValue}
        <span className={`arrow ${isOpen ? 'up' : 'down'}`}>
          {isOpen ? '▲' : '▼'}
        </span>
      </div>
      {isOpen && (
        <div className="dropdown-list">
          {options.length > 0 ? (
            options.map((opt, index) => (
              <div key={index} className="option-item">
                <span className="weight">{opt.displayWeight}{opt.unit}</span>
                <span className="arrow">→</span>
                <span className="count">{opt.packets} packets</span>
              </div>
            ))
          ) : (
            <div className="empty">No packaging options</div>
          )}
        </div>
      )}
    </div>
  );
};

const DataTableWithExpandableRows = ({
  columns,
  endpoint,
  secondaryTableConfig,
  keyFieldsConfig,
  onEdit,
  searchTerm,
  filterParams,
  hasSubGroups = false,
  subGroupConfig = {},
  onDelete,
  enableSeedAction = false,
  onSeedAction,
  updateKey, // Получаем updateKey
}) => {
  const [expandedRows, setExpandedRows] = useState({});
  const [expandedSubRows, setExpandedSubRows] = useState({});
  const [subGroupsData, setSubGroupsData] = useState({});
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: '', order: 'ASC' });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(1000);
  const [totalItems, setTotalItems] = useState(0);

  const handleSort = (key) => {
    setPage(1);
    setSortConfig((prev) => ({
      key,
      order: prev.key === key && prev.order === SORT_ORDER.ASC ? SORT_ORDER.DESC : SORT_ORDER.ASC,
    }));
  };

  const fetchSubGroups = async (rowId, parentRow) => {
    try {
      const params = keyFieldsConfig.reduce((acc, key) => {
        acc[key] = parentRow[key];
        return acc;
      }, {});

      const response = await api.get(subGroupConfig.endpoint, { params });
      setSubGroupsData((prev) => ({
        ...prev,
        [rowId]: response.data,
      }));
    } catch (error) {
      console.error('Error fetching subgroups:', error);
    }
  };

  useEffect(() => {
    let cancelled = false; // игнорируем ответы устаревших запросов (гонка при быстром вводе в поиск)
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await api.get(endpoint, {
          params: {
            page,
            limit,
            search: searchTerm,
            ...filterParams,
            sortKey: sortConfig.key,
            sortOrder: sortConfig.order,
          },
        });
        if (cancelled) return;
        const responseData = response.data;

        if (responseData?.data) {
          setData(responseData.data);
          setTotalItems(responseData.total || 0);
        } else {
          setError('Unexpected response format');
        }
      } catch (err) {
        if (!cancelled) setError('Failed to fetch data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [searchTerm, filterParams, sortConfig, page, limit, updateKey]); // Добавляем updateKey

  const toggleExpandRow = async (rowId, parentRow) => {
    const isExpanding = !expandedRows[rowId];

    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: isExpanding,
    }));

    if (isExpanding && hasSubGroups) {
      await fetchSubGroups(rowId, parentRow);
    }
  };

  const toggleExpandSubRow = (rowId, subRowId) => {
    setExpandedSubRows((prev) => ({
      ...prev,
      [`${rowId}-${subRowId}`]: !prev[`${rowId}-${subRowId}`],
    }));
  };

  const renderSubGroupRow = (rowId, subGroup, parentRow) => {
    const subRowId = subGroup[subGroupConfig.keyField];

    return (
      <React.Fragment key={`${rowId}-${subRowId}`}>
        <tr className="subgroup-row">
          <td colSpan={columns.length + 1}>
            <div className="subgroup-content">
              <button
                onClick={() => toggleExpandSubRow(rowId, subRowId)}
                className="table__expand-button"
              >
                {expandedSubRows[`${rowId}-${subRowId}`] ? '▲' : '▼'}
              </button>
              {subGroupConfig.columns.map((col) => (
                <span key={col.key} className="subgroup-field">
                  {subGroup[col.key]}
                </span>
              ))}
            </div>
          </td>
        </tr>
        {expandedSubRows[`${rowId}-${subRowId}`] && (
          <tr>
            <td colSpan={columns.length + 1}>
              <SecondaryTable
                parentRowId={parentRow}
                subGroup={subGroup}
                endpoint={secondaryTableConfig.endpointBase}
                columns={secondaryTableConfig.columns}
                keyFieldsConfig={secondaryTableConfig.keyFieldsConfig}
                onEdit={onEdit}
                enableHistory={secondaryTableConfig?.enableHistory}
                historyConfig={secondaryTableConfig?.historyConfig}
                queryParamsMapping={secondaryTableConfig.queryParamsMapping}
                enableSeedAction={secondaryTableConfig.enableSeedAction}
                onSeedAction={secondaryTableConfig.onSeedAction}
                seedsName={parentRow.combinedSeedsName}
                updateKey={secondaryTableConfig.updateKey} // Передаем updateKey
              />
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!data.length) return <div>No data available</div>;

  const totalPages = Math.ceil(totalItems / limit);

  return (
    <div className="expandable-table">
      <table className="table">
        <thead className="table__heading">
          <tr>
            <th></th>
            {columns.map((col) => (
              <th key={col.key} onClick={() => handleSort(col.key)} style={{ cursor: 'pointer' }}>
                {col.label}
                {sortConfig.key === col.key && (
                  <svg
                    className={`icon icon__sort ${
                      sortConfig.order === SORT_ORDER.ASC ? 'icon__sort--asc' : 'icon__sort--desc'
                    }`}
                    width="16"
                    height="16"
                  >
                    <use xlinkHref="#sort" />
                  </svg>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row) => {
            const rowId = keyFieldsConfig.map((key) => row[key]).join('-');
            return (
              <React.Fragment key={rowId}>
                <tr>
                  <td>
                    <button
                      className="table__expand-button"
                      onClick={() => toggleExpandRow(rowId, row)}
                    >
                      {expandedRows[rowId] ? '▲' : '▼'}
                    </button>
                  </td>
                  {columns.map((col) => (
                    <td key={col.key}>
                      {col.type === 'dropdown' ? (
                        <DropdownCell value={row[col.key]} endpoint={col.endpoint} row={row} paramKey={col.paramKey || 'seedsID'} />
                      ) : (
                        row[col.key] ?? '-'
                      )}
                    </td>
                  ))}
                </tr>
                {expandedRows[rowId] && (
                  hasSubGroups ? (
                    subGroupsData[rowId]?.map((subGroup) => renderSubGroupRow(rowId, subGroup, row))
                  ) : (
                    <tr>
                      <td colSpan={columns.length + 1}>
                        <div className="secondary-tables-container">
                          <div className="secondary-table-wrapper">
                            {secondaryTableConfig.title && (
                              <h4 className="secondary-table-title">{secondaryTableConfig.title}</h4>
                            )}
                            <SecondaryTable
                              parentRowId={rowId}
                              endpoint={`${secondaryTableConfig.endpointBase}?${keyFieldsConfig
                                .map((key) => `${key}=${row[key]}`)
                                .join('&')}`}
                              columns={secondaryTableConfig.columns}
                              keyFieldsConfig={keyFieldsConfig}
                              onEdit={onEdit}
                              enableHistory={secondaryTableConfig?.enableHistory}
                              historyConfig={secondaryTableConfig?.historyConfig}
                              enableEdit={secondaryTableConfig.enableEdit}
                              enableSeedAction={secondaryTableConfig.enableSeedAction}
                              onSeedAction={secondaryTableConfig.onSeedAction}
                              seedsName={row.combinedSeedsName}
                              updateKey={secondaryTableConfig.updateKey} // Передаем updateKey
                            />
                          </div>
                          {secondaryTableConfig.secondaryTable2 && (
                            <div className="secondary-table-wrapper">
                              {secondaryTableConfig.secondaryTable2.title && (
                                <h4 className="secondary-table-title">
                                  {secondaryTableConfig.secondaryTable2.title}
                                </h4>
                              )}
                              <SecondaryTable
                                parentRowId={rowId}
                                endpoint={`${secondaryTableConfig.secondaryTable2.endpointBase}?${keyFieldsConfig
                                  .map((key) => `${key}=${row[key]}`)
                                  .join('&')}`}
                                columns={secondaryTableConfig.secondaryTable2.columns}
                                keyFieldsConfig={keyFieldsConfig}
                                onEdit={onEdit}
                                enableHistory={secondaryTableConfig.secondaryTable2?.enableHistory}
                                enableEdit={secondaryTableConfig.secondaryTable2.enableEdit}
                                enableSeedAction={secondaryTableConfig.enableSeedAction}
                                onSeedAction={secondaryTableConfig.onSeedAction}
                                seedsName={row.combinedSeedsName}
                                updateKey={secondaryTableConfig.updateKey} // Передаем updateKey
                              />
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      <Pagination
        page={page}
        totalPages={totalPages}
        limit={limit}
        setPage={setPage}
        setLimit={setLimit}
      />
    </div>
  );
};

export default DataTableWithExpandableRows;